import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { useFacebookReachable } from '@/lib/useFacebookReachable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GoogleSignInButton } from '@/components/GoogleSignInButton'
import { ArrowLeft, Loader2 } from 'lucide-react'

export function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // In preregister mode, keep users on the marketing site after login.
  const redirectTo = searchParams.get('redirectTo') || '/'
  const { user, loading: authLoading, login, loginWithGoogle } = useAuth()
  const facebookReachable = useFacebookReachable()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (user) {
    navigate(redirectTo, { replace: true })
    return null
  }
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading...</div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!email || !password) {
      setError('Email and password are required')
      return
    }
    setIsSubmitting(true)
    try {
      await login(email, password)
      navigate(redirectTo, { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid email or password')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-md mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] bg-clip-text text-transparent">
            Welcome Back
          </h1>
          <p className="text-xl text-gray-400">Sign in to continue watching</p>
        </div>
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 rounded-lg bg-[#D72638]/20 border border-[#D72638] text-[#D72638]">{error}</div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">Email Address</label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white border-none outline-none"
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">Password</label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white border-none outline-none"
                placeholder="Enter your password"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D72638] hover:bg-[#D72638]/90 text-white py-6 text-lg font-semibold rounded-xl"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            <div className="pt-2 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-800" />
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  Or continue with
                </span>
                <div className="flex-1 h-px bg-gray-800" />
              </div>
              <div className={`grid gap-3 ${facebookReachable ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <GoogleSignInButton
                  className="flex justify-center"
                  onSuccess={async (credential) => {
                    await loginWithGoogle(credential)
                    navigate(redirectTo, { replace: true })
                  }}
                  onError={(message) => setError(message)}
                />
                {facebookReachable === true && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full bg-black/40 border-gray-700 hover:bg-black/60 font-medium"
                    onClick={() => {
                      const facebookAuthUrl =
                        import.meta.env.VITE_FACEBOOK_AUTH_URL ||
                        import.meta.env.NEXT_PUBLIC_FACEBOOK_AUTH_URL;
                      if (facebookAuthUrl) {
                        window.location.href = facebookAuthUrl as string;
                      }
                    }}
                  >
                    <span className="mr-2 text-lg">f</span>
                    <span>Facebook</span>
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
        <div className="mt-8 text-center">
          <p className="text-gray-400">
            Don't have an account? <Link to="/signup" className="text-[#D4AF37] hover:underline">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
